import { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  AppInput,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppTextArea,
  AppNoInfo,
} from '@/layout/AppComponents';
import Translator from '../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import ImageCropper from '../../../layout/components/form/ImageCropper';
import NoInfo from '../../../layout/components/helpers/NoInfo';
import SpinnerLoader from '../../../layout/components/helpers/SpinnerLoader';
import NotificationAlert from '../../../layout/components/helpers/notificationAlert';
import { axiosPath } from '../../../routing/helpers/router';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';

const ModuleChemicalTradingProductHomeScreen = () => {
  const { moduleBridgeHex, id } = useParams();
  const isMounted = useIsMounted();
  const global = useSelector((state) => state.global);
  const [product, setProduct] = useState(null);
  const [moduleData, setModuleData] = useState();
  const [productOptions, setProductOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isContacting, setIsContacting] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  // > LOAD PRODUCT //
  useEffect(() => {
    if (id) {
      setLoading(true);
      axios
        .post(axiosPath("api_r_user_module_chemicalTrading_product_get"), {
          moduleBridgeHex,
          id,
        })
        .then((r) => {
          if (isMounted.current) {
            setProduct(r.data);
            setLoading(false);
          }
        });
    }
  }, []);
  // < LOAD PRODUCT //

  // > LOAD PRODUCTS OPTIONS //
  useEffect(() => {
    axios
      .post(axiosPath("api_r_user_module_chemicalTrading_product_options"), {
        moduleBridgeHex,
      })
      .then((r) => {
        if (isMounted.current) {
          setProductOptions(r.data);
        }
      });
  }, []);
  // > LOAD PRODUCTS OPTIONS //

  // > LOAD MODULE DATA //
  useEffect(() => {
    axios
      .post(axiosPath("api_r_user_module_chemicalTrading_getModuleData"), {
        moduleBridgeHex,
      })
      .then((r) => {
        if (isMounted.current) setModuleData(r.data);
      });
  }, []);
  // > LOAD MODULE DATA //

  // < CONTACT FORM
  const contact = async (message) => {
    try {
      const response = axios.post(
        axiosPath("api_r_user_module_chemicalTrading_contactSeller"),
        {
          message,
          moduleBridgeHex,
          productId: id,
        }
      );
      return response.data;
    } catch (err) {}
  };

  const handleContactForm = (values) => {
    setIsContacting(true);
    contact(values.message)
      .then(() => {
        if (isMounted.current) {
          setIsContacting(false);
          setShowContactModal(false);
          NotificationAlert({
            title: 'Completado',
            message: 'Se ha enviado el mensaje',
            type: 'success',
            duration: 1000,
          });
        }
      })
      .catch(() => {
        NotificationAlert({
          title: 'Error',
          message: 'Se ha producido un error',
          type: 'danger',
          duration: 1000,
        });

        if (isMounted.current) setIsContacting(false);
      });
  };
  // < CONTACT FORM

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    message: Yup.string().required(),
    hasAcceptTerms: Yup.bool()
      .required()
      .oneOf([true]),
  });
  // < FORM SCHEMA

    if (!moduleData)
      return <AppNoInfo />

  return (
    <div className="pl-2">
      {!loading && (
        <BreadcrumbHandler
          paths={[
            {
              name: `${moduleData.moduleName} - ${ReactDOMServer.renderToString(
                <Translator
                  file="module-chemicalTrading"
                  name="products.list"
                />
              )}`,
              route: {
                name: 'user_module_chemicalTrading_product_list',
                params: { moduleBridgeHex },
              },
            },
            { name: product ? product.comercialName : '' },
          ]}
        />
      )}
      <section className="content">
        <div className="row">
          <div className="col-lg-12 mt-3">
            {product?.isArchived && (
              <span className="btn btn-danger btn-sm float-right">
                {ReactDOMServer.renderToString(
                  <Translator
                    file="module-chemicalTrading"
                    name="archived.product"
                  />
                )}
              </span>
            )}
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <Card className="card" isArchived={product?.isArchived}>
              <div className="card-body">
                {loading ? (
                  <NoInfo icon={<SpinnerLoader size={40} />} />
                ) : product ? (
                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <div className="row">
                        <div className="col-lg-12">
                          <ImageCropper
                            cropShape="rect"
                            image={product.image}
                            width={200}
                            height={200}
                            disabled
                          />
                        </div>
                        {!product.isOwn && (
                          <div className="col-lg-12 text-center">
                            <button
                              className="btn btn-info btn-sm mt-2"
                              onClick={() => setShowContactModal(true)}
                            >
                              <AppRemixIcon icon="mail" />
                              Contacto
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row">
                        <div className="col-lg-12">
                          <AppInput
                            icon={<AppRemixIcon icon="font-color" noVariants />}
                            title={
                              <Translator
                                file="module-chemicalTrading"
                                name="comercial.name"
                              />
                            }
                            value={product.comercialName}
                            readOnly
                          />
                        </div>
                        <div className="col-lg-12">
                          <AppInput
                            icon={<AppRemixIcon icon="font-color" noVariants />}
                            title={
                              <Translator
                                file="module-chemicalTrading"
                                name="chemical.name"
                              />
                            }
                            value={product.chemicalName}
                            readOnly
                          />
                        </div>
                        <div className="col-lg-12">
                          <AppInput
                            icon={<AppRemixIcon icon="apps-2" />}
                            title={
                              <Translator
                                file="module-chemicalTrading"
                                name="container.type"
                              />
                            }
                            value={
                              productOptions?.containerTypes.find(
                                (ct) => ct.value === product.containerType
                              )?.label ?? ''
                            }
                            // value={product.containerType}
                            readOnly
                          />
                        </div>
                        <div className="col-lg-12">
                          <AppInputNumber
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-chemicalTrading"
                                name="quantity"
                              />
                            )}
                            icon={<AppRemixIcon icon="hashtag" noVariants />}
                            value={product.quantity}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <AppInput
                        icon={<AppRemixIcon icon="calendar" />}
                        title={
                          <Translator
                            file="module-chemicalTrading"
                            name="expire.date"
                          />
                        }
                        value={product.expireDate}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInput
                        icon={<AppRemixIcon icon="calendar" />}
                        title={
                          <Translator
                            file="module-chemicalTrading"
                            name="date.stamp"
                          />
                        }
                        value={product.dateStamp}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInput
                        icon={<AppRemixIcon icon="calendar" />}
                        title={
                          <Translator
                            file="module-chemicalTrading"
                            name="update.date"
                          />
                        }
                        value={product.lastEditionDate}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInput
                        icon={<AppRemixIcon icon="calendar" />}
                        title={
                          <Translator
                            file="module-chemicalTrading"
                            name="close.date"
                          />
                        }
                        value={product.closeDate}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <AppTextArea
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-chemicalTrading"
                            name="sale.reason"
                          />
                        )}
                        value={product.saleReason}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <AppTextArea
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-chemicalTrading"
                            name="contact.data"
                          />
                        )}
                        value={product.contactData}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <AppTextArea
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-chemicalTrading"
                            name="product.use"
                          />
                        )}
                        value={product.productUse}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <AppTextArea
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-chemicalTrading"
                            name="features"
                          />
                        )}
                        value={product.features}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInput
                        icon={<AppRemixIcon icon="map-pin" />}
                        title={
                          <Translator
                            file="module-chemicalTrading"
                            name="store.place"
                          />
                        }
                        value={product.storePlace}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInput
                        icon={<AppRemixIcon icon="alert" />}
                        title={
                          <Translator
                            file="module-chemicalTrading"
                            name="risk"
                          />
                        }
                        value={product.risk}
                        readOnly
                      />
                    </div>
                    <div className="col-lg-12">
                      {product?.files && Object.keys(product.files).length > 0 && (
                        <div className="row">
                          <div className="col-lg-12 mt-4">
                            <label>
                              <Translator
                                file="module-chemicalTrading"
                                name="files"
                              />
                            </label>
                            <div>
                              {Object.keys(product.files).map((salt, index) => (
                                <div key={index} className="mt-2">
                                  <a
                                    target="_blank"
                                    title={product.files[salt].name}
                                    className="mr-3"
                                    href={`${global.urlData.userApiUrl}m/chemical-trading/get-product-file/${moduleBridgeHex}/${product.id}/${salt}`}
                                    rel="noreferrer"
                                  >
                                    <small>Descargar</small>
                                  </a>
                                  <label className="mb-2 pointer font-weight-light">
                                    {product.files[salt].name}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>El producto no existe</>
                )}
              </div>
            </Card>
          </div>
        </div>
        <AppModal
          show={showContactModal}
          title={<Translator file="module-chemicalTrading" name="contact" />}
          onClose={() => setShowContactModal(false)}
        >
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
            initialValues={{
              message: '',
              hasAcceptTerms: false,
            }}
            validationSchema={validationSchema}
            onSubmit={handleContactForm}
          >
            {({ setFieldValue, handleSubmit, values, errors }) => (
              <>
                <div className="row">
                  <div className="col-lg-12">
                    <AppTextArea
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-chemicalTrading"
                          name="message"
                        />
                      )}
                      onChange={(v) => setFieldValue('message', v)}
                      value={values.message}
                      error={!!errors.message}
                      isClearable
                      required
                    />
                  </div>
                  <div className="col-lg-12">
                    <label className="col-form-label mr-3 pointer">
                      <input
                        type="checkbox"
                        className="mr-1"
                        value={values.hasAcceptTerms}
                        onChange={() =>
                          setFieldValue(
                            'hasAcceptTerms',
                            !values.hasAcceptTerms
                          )
                        }
                        checked={values.hasAcceptTerms}
                      />
                      <Translator
                        file="module-chemicalTrading"
                        name="has.accept.terms.text"
                      />
                    </label>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 text-right">
                    <button
                      className="btn btn-info mt-2"
                      type="submit"
                      onClick={handleSubmit}
                      disabled={isContacting}
                    >
                      <Translator file="module-chemicalTrading" name="send" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </Formik>
        </AppModal>
      </section>
    </div>
  );
};

export default ModuleChemicalTradingProductHomeScreen;

const Card = styled.div`
  border: ${(props) => (props.isArchived ? '1px solid red' : 'border: none')};
`;
